import dayjs from "dayjs";
import type { TransactionItem } from "@models/adminMonitoring";

/**
 * Presentation helpers for metering-driven monitoring. Raw module / service
 * values stay the identity everywhere (filters, URLs); a friendly name is only
 * ever a label, and an unknown value is shown as is.
 */

const MODULE_LABELS: Record<string, string> = {
  extraction: "Extraction",
  intellixtract: "Extraction",
  mapping: "Mapping",
  search: "Search",
  embedding: "Embedding",
  "xplatform-generation": "Generation",
  translation: "Translation",
};

export const moduleLabel = (value: string | null | undefined): string =>
  value ? (MODULE_LABELS[value] ?? value) : "unknown";

/** `1,200 characters · 3 pages`; `-` when nothing was measured. */
export const formatQuantities = (quantities: Record<string, string> | null | undefined): string => {
  const entries = Object.entries(quantities ?? {});
  if (!entries.length) return "-";
  return entries.map(([unit, amount]) => `${formatCount(Number(amount))} ${unit.replace(/_/g, " ")}`).join(" · ");
};

/** Money of one currency. Amounts stay decimal strings until display. */
export const formatMoney = (amount: string | number | null | undefined, currency: string): string => {
  if (amount === null || amount === undefined) return "-";
  const value = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(value)) return String(amount);
  const abs = Math.abs(value);
  const digits = abs === 0 ? 2 : abs < 0.01 ? 4 : abs < 100 ? 2 : 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: Math.min(digits, 2),
      maximumFractionDigits: digits,
    }).format(value);
  } catch {
    return `${value.toFixed(digits)} ${currency}`;
  }
};

export const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? "-" : new Intl.NumberFormat().format(value);

export const formatCompact = (value: number): string =>
  new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value);

export const formatDateTime = (iso: string | null | undefined): string =>
  iso ? dayjs(iso).format("YYYY-MM-DD HH:mm:ss") : "-";

/** The reference of a transaction as one readable line (never a business id it does not have). */
export const describeReference = (item: Pick<TransactionItem, "kind" | "external_ref_type" | "external_ref_id">): string => {
  if (item.kind === "event") return "-";
  return [item.external_ref_type, item.external_ref_id].filter(Boolean).join(" · ") || "-";
};

/** Compact one-line timestamp for tables (`Oct 8, 23:21`); the full value goes in a tooltip. */
export const formatShortDateTime = (iso: string | null | undefined): string =>
  iso ? dayjs(iso).format("MMM D, HH:mm") : "-";

/** `JOB#c1745d67-b4da-…` -> `JOB · c1745d67`: the prefix and the first id block. */
export const shortRef = (value: string | null | undefined): string => {
  if (!value) return "";
  const [prefix, ...rest] = value.split("#");
  const id = rest.join("#");
  if (!id) return prefix;
  return `${prefix} · ${id.split(/[-_]/)[0].slice(0, 12)}`;
};
